import React, { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import userStore from '../Stores/User';
import { 
  getConfigByRoute, 
  isPartnerRoute, 
  isCouponRoute, 
  isReferralRoute,
  getCouponCodeFromRoute 
} from '../constants/partners';
import { 
  trackPartnerRoute, 
  trackCouponRoute, 
  trackReferralRoute 
} from '../audiblogsApi';
import LandingA from '../Pages/Landing/LandingA';
import ChooseVoice from '../Pages/Onboarding/Pages/ChooseVoice';
import ReferralLanding from './ReferralLanding';
import Loader from './Loader';
import mixpanel from 'mixpanel-browser';

const DynamicRouter = observer(() => {
  const { route, couponCode, referralUuid, partnerRoute } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Determine the actual route from the various params
  const actualRoute = route || couponCode || referralUuid || partnerRoute;

  useEffect(() => {
    const handleRoute = async () => {
      if (!actualRoute) {
        setError('Invalid route');
        setIsLoading(false);
        return;
      }



      try {
        console.log('DynamicRouter: Processing route', actualRoute);
        const routeConfig = getConfigByRoute(actualRoute);
        console.log('DynamicRouter: Route config', routeConfig);
        
        if (!routeConfig) {
          console.log('DynamicRouter: No route config found for route:', actualRoute);
          // Don't set error for undefined routes - let React Router handle them
          setIsLoading(false);
          return null;
        }

        setConfig(routeConfig);

        // Track the route visit based on type
        console.log('DynamicRouter: Route type detection', {
          actualRoute,
          isPartner: isPartnerRoute(actualRoute),
          isCoupon: isCouponRoute(actualRoute),
          isReferral: isReferralRoute(actualRoute)
        });
        
        if (isPartnerRoute(actualRoute)) {
          await trackPartnerRoute(actualRoute);
          mixpanel.track('Partner Route Visited', {
            partner: actualRoute,
            utm_source: routeConfig.utmSource
          });
        } else if (isCouponRoute(actualRoute)) {
          const couponCode = getCouponCodeFromRoute(actualRoute);
          await trackCouponRoute(couponCode);
          mixpanel.track('Coupon Route Visited', {
            coupon_code: couponCode,
            utm_source: routeConfig.utmSource
          });
        } else if (isReferralRoute(actualRoute)) {
          const referralUuid = actualRoute;
          await trackReferralRoute(referralUuid);
          mixpanel.track('Referral Route Visited', {
            referral_uuid: referralUuid,
            utm_source: routeConfig.utmSource
          });
        }

        // Set UTM source in mixpanel
        mixpanel.people.set({
          utm_source: routeConfig.utmSource,
          last_partner_route: route
        });

        setIsLoading(false);
      } catch (error) {
        console.error('Error handling route:', error);
        setError('Failed to load route');
        setIsLoading(false);
      }
    };

    handleRoute();
  }, [actualRoute]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: '#fff',
        fontSize: '18px',
        textAlign: 'center'
      }}>
        <div>{error}</div>
        <button
          onClick={() => history.push('/')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Go Home
        </button>
      </div>
    );
  }

  // Handle different route types
  if (isPartnerRoute(actualRoute)) {
    // For partner routes, check if user is signed in first
    if (userStore.isSignedIn === true || userStore.user) {
      console.log('DynamicRouter: User signed in on partner route, redirecting to /add');
      // Store message in localStorage to show after redirect
      localStorage.setItem('partnerDiscountMessage', `Only new customers are eligible for ${config.name} discounts.`);
      // Redirect to /add using React Router
      history.push('/add');
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: '#fff',
          fontSize: '18px'
        }}>
          Redirecting to /add...
        </div>
      );
    }
    
    // For partner routes, show the specified landing page with custom content
    if (config.landingPage === 'LandingA') {
      // Store partner info for when user signs up
      const partnerData = {
        code: config.couponCode,
        config: config,
        returnUrl: `/onboarding/ChooseVoice?coupon=${config.couponCode}&utm_source=${config.utmSource}`
      };
      console.log('DynamicRouter: Storing partner data', partnerData);
      localStorage.setItem('pendingCoupon', JSON.stringify(partnerData));
      
      return <LandingA partnerConfig={config} />;
    }
    // Add more landing page options as needed
  }

  if (isCouponRoute(actualRoute)) {
    console.log('DynamicRouter: Handling coupon route', { actualRoute, config });
    // For coupon routes, redirect to ChooseVoice with auto-applied coupon
    if (!userStore.isSignedIn) {
      const couponCode = getCouponCodeFromRoute(actualRoute);
      console.log('DynamicRouter: User not signed in, storing coupon', couponCode);
      // Store coupon info in localStorage and redirect to signin
      localStorage.setItem('pendingCoupon', JSON.stringify({
        code: couponCode,
        config: config,
        returnUrl: `/onboarding/ChooseVoice?coupon=${couponCode}`
      }));
      // Use window.location for immediate redirect to avoid rendering issues
      window.location.href = '/signin?redirected_from=coupon';
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: '#fff',
          fontSize: '18px'
        }}>
          Redirecting to signin...
        </div>
      );
    }
    
    console.log('DynamicRouter: User signed in, going to ChooseVoice');
    // User is signed in, go directly to ChooseVoice
    return <ChooseVoice couponConfig={config} />;
  }

  if (isReferralRoute(actualRoute)) {
    // For referral routes, show referral landing page
    const referralUuid = actualRoute; // actualRoute is already the UUID from /referral/:referralUuid
    
    // Always show the referral landing page, regardless of sign-in status
    return <ReferralLanding referralUuid={referralUuid} />;
  }

  return null;
});

export default DynamicRouter;
